import React, { createContext, useContext, useEffect, useState } from "react";
import {loginUser, getUserDetails, logoutUser as apiLogoutUser, refreshAccessToken} from "../Services/ApiCallsServices/AuthenticationApi";
import { useNavigate } from "react-router-dom";
import { getAuthToken, setAuthToken } from "../Services/ApiCallsServices/SecuredApi";
import { persistor } from '../store/store';

/**
 * AuthContext provides global authentication state and methods
 * for logging in, logging out, and retrieving the current user's profile.
 */
const AuthContext = createContext();

/**
 * Provides authentication and context for managing user authentication state.
 *
 * This function initializes and manages the authentication state using React's `useState` and `useEffect`.
 * It wraps its children components with `AuthContext.Provider` to make authentication state and related
 * functions available throughout the component tree.
 *
 * @param {Object} param0 - An object containing component props.
 * @param {React.ReactNode} param0.children - The child components that should have access to the authentication context.
 * @returns {JSX.Element} A JSX element wrapping the provided children with the authentication context.
 */
export const AuthProvider = ({ children }) => {
    const [auth, setAuth] = useState({
        token: null,
        user: null,
        isAuthenticated: false,
    });
    const navigate = useNavigate();


    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const storedToken = getAuthToken();
                if (storedToken) {
                    setAuthToken(storedToken);
                    await fetchProfile(storedToken);
                } else {
                    const token = await refreshAccessToken();
                    if (token) {
                        setAuthToken(token);
                        await fetchProfile(token);
                    } else {
                        console.log('No token available from refresh');
                    }
                }
            } catch (err) {
                console.error("Auth initialization error:", err);
            } finally {
                console.log('Auth init complete');
                setLoading(false);
            }
        })();
    }, []);

    /**
     * Authenticates a user by verifying the provided credentials
     * and retrieves user-specific details upon successful login.
     *
     * The function performs the following steps:
     * - Calls loginUser with the provided username and password to
     *   obtain an access token and refresh token.
     * - Encrypts and stores the retrieved tokens for secure usage.
     * - Fetches user details using the access token.
     * - Sets authentication state,*/
    const login = async (username, password) => {
        try {
            const { access_token } = await loginUser(username, password);

            // Save to in-memory state and SecuredApi closure
            setAuthToken(access_token); // Sets module-level token
            setAuth({ token: access_token, user: null, isAuthenticated: true });

            const user = await getUserDetails(access_token);

            setAuth({ token: access_token, user, isAuthenticated: true });

            navigate(user.role === "user" ? "/dashboard" : "/driver-dashboard");
        } catch (err) {
            console.error(err);
            throw err;
        }
    };



    /**
     * Asynchronously fetches the user profile based on the given token.
     *
     * This function retrieves user details using the provided token.
     * If successful, it updates authentication state by setting the token,
     * user details, and marking the state as authenticated.
     * If an error occurs during the process, it logs the error and initiates the logout process.
     *
     * @param {string} token - The authentication token used to fetch user profile details.
     * @returns {Promise<void>} A promise that resolves when the profile is successfully fetched and authentication state is updated,
     * or rejects in case of an error leading to logout.
     */
    const fetchProfile = async (token) => {
        try {
            const user = await getUserDetails(token);
            setAuth({ token, user, isAuthenticated: true });
        } catch (err) {
            console.error("Failed to fetch user profile:", err);
            doLogout();
        }
    };


    /**
     * Logs out the currently authenticated user.
     *
     * This function performs the following actions:
     * - Removes the stored authentication token from local storage.
     * - Calls the API to log out the user on the backend. Errors in this step are caught and ignored.
     * - Updates the authentication state to signify that the user is no longer authenticated.
     * - Redirects the user to the login page.
     *
     * This function is asynchronous and may include API interactions.
     */
    const doLogout = async () => {
        try {
            await apiLogoutUser();
        } catch (err) {
            console.warn("Logout error (ignored):", err);
        }
        // Clear the persisted data by Redux
        await persistor.purge();
        // Clear in-memory access token
        setAuthToken(null);
        setAuth({ token: null, user: null, isAuthenticated: false });

        navigate("/login");
    };


    return (
        <AuthContext.Provider
            value={{ auth, login, logout: doLogout, loading }}
        >
            {children}
        </AuthContext.Provider>
    );
};

/**
 * Custom hook to access the authentication context from components.
 */
export const useAuth = () => useContext(AuthContext);